import { Accordion, Button, Flex, FullWidthPage, Heading, Text } from "@wayflyer/flyui";
import {
  IconDollarCircle24Line,
  IconClock24Line,
  IconProhibition24Line,
  IconCreditCardReload24Line,
} from "@wayflyer/flyui-icons/24/line";
import {
  AccordionGridItem,
  Card,
  CardImageContainer,
  CardImg,
  DialogVideo,
  FullColGridItem,
  GridLayout,
  HalfColGridItem,
  HeroList,
  StyledLi,
  getImageSrcSet,
} from "shared-ui";
import { useIntl } from "react-intl";

const spongelleVideo = {
  playbackId: "7sVY3008exzNPwvthdpfegb2Bcpkj01H700JLHxFlxptVE",
  title: "Spongellé",
};

const trueClassicVideo = {
  playbackId: "tioyWcJrhfjJn8oEmiT02DR1PvHIfqiuKjnDCekGtdtg",
  title: "True Classic",
};

const petshopVideo = {
  playbackId: "3kUm6VcTQFxET443f2yKA3Emm9mnUR253HfPMbGYi01Y",
  title: "Petshop.co.uk",
};

export function LandingScreen({ onSubmit, isPending }: { onSubmit: () => void; isPending: boolean }) {
  const intl = useIntl();

  return (
    <FullWidthPage
      title={intl.formatMessage({
        id: "funder: landing title",
        defaultMessage: "We power the world's fastest-growing consumer brands with purpose-built financing.",
      })}
    >
      <Flex direction="column" gap="12">
        <GridLayout>
          <HalfColGridItem>
            <Text>
              {intl.formatMessage({
                id: "funder: landing financing partner",
                defaultMessage:
                  "Wayflyer is the trusted financing partner of 3,250+ businesses globally, from early-stage ventures through to internationally recognized brands.",
              })}
            </Text>
            <HeroList>
              <StyledLi>
                <IconDollarCircle24Line />
                <Text>
                  {intl.formatMessage({
                    id: "funder: landing funds available",
                    defaultMessage: "$10k to $20m available to fund your growth.",
                  })}
                </Text>
              </StyledLi>
              <StyledLi>
                <IconProhibition24Line />
                <Text>
                  {intl.formatMessage({
                    id: "funder: landing no equity dilution",
                    defaultMessage: "No equity dilution, just a simple fixed fee.",
                  })}
                </Text>
              </StyledLi>
              <StyledLi>
                <IconCreditCardReload24Line />
                <Text>
                  {intl.formatMessage({
                    id: "funder: landing no personal guarantee",
                    defaultMessage: "No personal guarantee or collateral required.",
                  })}
                </Text>
              </StyledLi>
              <StyledLi>
                <IconClock24Line />
                <Text>
                  {intl.formatMessage({
                    id: "funder: landing funds deployed timeline",
                    defaultMessage: "Funds deployed to you in as little as 24 hours.",
                  })}
                </Text>
              </StyledLi>
            </HeroList>
            <div style={{ display: "inline-flex", marginTop: "20px" }}>
              <Button variant="Primary" onClick={onSubmit} loading={isPending}>
                {intl.formatMessage({
                  id: "funder: landing apply for funds cta",
                  defaultMessage: "Apply for funds",
                })}
              </Button>
            </div>
          </HalfColGridItem>
          <FullColGridItem />
        </GridLayout>
        <Flex direction="column" gap="5">
          <GridLayout>
            <HalfColGridItem>
              <Heading element="h2" size="2xl">
                {intl.formatMessage({
                  id: "funder: landing best products",
                  defaultMessage: "We're putting the best products in the hands of more consumers worldwide.",
                })}
              </Heading>
            </HalfColGridItem>
          </GridLayout>
          <GridLayout>
            <DialogVideo {...petshopVideo}>
              <Card>
                <CardImageContainer>
                  <CardImg
                    alt="Petshop"
                    srcSet={getImageSrcSet(
                      "https://cdn.sanity.io/images/1aendizt/production/c093590460e24792724c7cf50db0f740daaea755-1834x1028.webp"
                    )}
                    src="https://cdn.sanity.io/images/1aendizt/production/c093590460e24792724c7cf50db0f740daaea755-1834x1028.webp?w=3840&amp;q=75"
                  />
                </CardImageContainer>
                <Text>
                  {intl.formatMessage({
                    id: "funder: landing petshop",
                    defaultMessage: "Off the leash: Petshop.co.uk unlocked operational excellence with Wayflyer",
                  })}
                </Text>
              </Card>
            </DialogVideo>
            <DialogVideo {...trueClassicVideo}>
              <Card>
                <CardImageContainer>
                  <CardImg
                    alt="True classic"
                    srcSet={getImageSrcSet(
                      "https://cdn.sanity.io/images/1aendizt/production/1403998e1102fa811c271926d430eefb8aea2949-1820x1024.webp"
                    )}
                    src="https://cdn.sanity.io/images/1aendizt/production/1403998e1102fa811c271926d430eefb8aea2949-1820x1024.webp?w=3840&amp;q=75"
                  />
                </CardImageContainer>
                <Text>
                  {intl.formatMessage({
                    id: "funder: landing true classic",
                    defaultMessage: "Style and substance: True Classic's astounding growth with Wayflyer",
                  })}
                </Text>
              </Card>
            </DialogVideo>
            <DialogVideo {...spongelleVideo}>
              <Card>
                <CardImageContainer>
                  <CardImg
                    alt="Spongellé"
                    srcSet={getImageSrcSet(
                      "https://cdn.sanity.io/images/1aendizt/production/0ffe6da326c8e9de3db95dbf7c41624b4083d91f-2734x1534.webp"
                    )}
                    src="https://cdn.sanity.io/images/1aendizt/production/0ffe6da326c8e9de3db95dbf7c41624b4083d91f-2734x1534.webp?w=3840&amp;q=75"
                  />
                </CardImageContainer>
                <Text>
                  {intl.formatMessage({
                    id: "funder: landing spongelle",
                    defaultMessage: "From suds to success: Spongellé soaked up omnichannel growth with Wayflyer",
                  })}
                </Text>
              </Card>
            </DialogVideo>
          </GridLayout>
        </Flex>
        <GridLayout>
          <AccordionGridItem>
            <Heading element="h2" size="2xl">
              {intl.formatMessage({
                id: "funder: faq",
                defaultMessage: "Frequently asked questions",
              })}
            </Heading>
            <Accordion
              items={[
                {
                  header: intl.formatMessage({
                    id: "funder: faq how long title",
                    defaultMessage: "How long does it take to get funding from Wayflyer?",
                  }),
                  body: intl.formatMessage({
                    id: "funder: faq how long description",
                    defaultMessage:
                      "You can sign up and request funding in less than 5 minutes. Our team will then be in touch within 24 hours to discuss your offers. Once you're happy with an offer, funds can be in your account within 1-3 days (depending on how long it takes your bank to receive the funds).",
                  }),
                },

                {
                  header: intl.formatMessage({
                    id: "funder: how much can I get title",
                    defaultMessage: "How much can I get from Wayflyer?",
                  }),
                  body: intl.formatMessage({
                    id: "funder: how much can I get description",
                    defaultMessage:
                      "We can provide up to $20 million in funding. Each company is analysed on a case by case basis so once you sign up and connect your platforms, we will create the best tailored offers for you.",
                  }),
                },
                {
                  header: intl.formatMessage({
                    id: "funder: how are offers generated title",
                    defaultMessage: "How are the funding offers generated?",
                  }),
                  body: intl.formatMessage({
                    id: "funder: how are offers generated description",
                    defaultMessage:
                      "Funding offers are based on several factors, including your financial performance and historic marketing performance. The more revenue you generate and the better your marketing performance, the more funding that will be available to you. We will always generate a number of funding offers for you, allowing you to choose the one that works best for your needs.",
                  }),
                },
                {
                  header: intl.formatMessage({
                    id: "funder: cost of funding title",
                    defaultMessage: "What is the cost of funding?",
                  }),
                  body: intl.formatMessage({
                    id: "funder: cost of funding description",
                    defaultMessage:
                      "The only cost of funding is the % transaction fee applied to the cash advance amount. This is usually between 2% and 8% but depends on certain factors such as the specifics of your business and how much funding you want.",
                  }),
                },
              ]}
            />
          </AccordionGridItem>
        </GridLayout>
      </Flex>
    </FullWidthPage>
  );
}
